import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { GetBeacon4PerformanceNetworkOverviewFilters } from '../requests/Beacon4PerformanceNetworkOverviewRequests';
import { GetBeacon4PerformanceNetworkOverviewActivityMarkers } from '../requests/Beacon4PerformanceNetworkOverviewActivitiesRequests';

// PerformanceNetworkOverview's Side/Polygon/Vendor/JV ID lookup — scoped to
// the selected Tech+KPI's source view and date range
// (Beacon4PerformanceNetworkOverview_filters_data). Dispatched whenever
// Tech/KPI or the date range changes (see Beacon4FilterBar).
export const loadBeacon4PerformanceNetworkOverviewFilters = createAsyncThunk(
	'beacon4Filters/loadPerformanceNetworkOverviewFilters',
	async ({ tech, mainKpiKey, dateFrom, dateTo }, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4PerformanceNetworkOverviewFilters({ tech, mainKpiKey, dateFrom, dateTo });
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

// Activities overlay ("Activities" entry in the Options panel, reusing data
// from the Activity Tracker module). Strictly scoped to the Network
// Overview's active dateFrom/dateTo window — the backend only returns
// activities whose StartDate/EndDate are fully contained in that window
// (contenção total, not partial overlap). Re-fires whenever dateFrom/dateTo
// change while the Activities panel is visible (see Beacon4FilterBar).
// side/vendor: the SAME comma-joined values as the "Filters" group's
// Side/Vendor multiselects (perfSide/perfVendor) — passed straight through
// so activities outside what the user already filtered the dashboard to
// don't show up in the Activities dropdown or as markers either.
export const loadBeacon4PerformanceNetworkOverviewActivities = createAsyncThunk(
	'beacon4Filters/loadPerformanceNetworkOverviewActivities',
	async ({ dateFrom, dateTo, side = '', vendor = '' }, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4PerformanceNetworkOverviewActivityMarkers(dateFrom, dateTo, side, vendor);
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

// Cascade helper for the Activities overlay's Team > Type > Activity
// hierarchy. Tech ISN'T a selection made within this cascade — it's
// whatever's picked in "KPI Selection" — so this only ever narrows by it,
// never resets it; `team` and `type` are the genuine selections here, same
// role Side/Polygon play for Vendor/JV ID below. Team sits ABOVE Type (it's
// the entry's assigned team, feature/Type is a property of the entry itself
// independent of team) — narrowing by Team first is what makes Type's own
// option list only show types that actually exist for the chosen team(s).
function computeActivityCascade(list, { tech, team, type }) {
	const selTeams = team ? team.split(',').map(v => v.trim()).filter(Boolean) : [];
	const selTypes = type ? type.split(',').map(v => v.trim()).filter(Boolean) : [];
	const byTech = (list ?? []).filter(a => !tech || a.technology === tech);
	const byTeam = byTech.filter(a => selTeams.length === 0 || selTeams.includes(a.teamName));
	const byType = byTeam.filter(a => selTypes.length === 0 || selTypes.includes(a.feature));
	return {
		teams:      [...new Set(byTech.map(a => a.teamName).filter(Boolean))].sort(),
		types:      [...new Set(byTeam.map(a => a.feature).filter(Boolean))].sort(),
		activities: byType,
	};
}

// Cascade helper for the Side > Polygon > Vendor > JV ID hierarchy. Pairs are
// tightly scoped to one Tech+KPI+date range, so there's no need for a
// reverse-derivation JV-ID-picked-first logic — picking a JV ID directly
// just doesn't need to narrow Side/Polygon/Vendor in a set this small.
function computePerfCascade(pairs, { side, polygon, vendor }) {
	const selSides    = side    ? side.split(',').map(v => v.trim()).filter(Boolean)    : [];
	const selPolygons = polygon ? polygon.split(',').map(v => v.trim()).filter(Boolean) : [];
	const selVendors  = vendor  ? vendor.split(',').map(v => v.trim()).filter(Boolean)  : [];

	const bySide    = pairs.filter(p => selSides.length === 0 || selSides.includes(p.side));
	const byPolygon = bySide.filter(p => selPolygons.length === 0 || selPolygons.includes(p.polygon));
	const byVendor  = byPolygon.filter(p => selVendors.length === 0 || selVendors.includes(p.vendor));

	return {
		sides:    [...new Set(pairs.map(p => p.side))].sort(),
		polygons: [...new Set(bySide.map(p => p.polygon))].sort(),
		vendors:  [...new Set(byPolygon.map(p => p.vendor).filter(Boolean))].sort(),
		jvids:    [...new Set(byVendor.map(p => p.jvId).filter(Boolean))].sort(),
	};
}

// Given a comma-separated selection and the list of values still valid for
// the new source, keeps whichever selected values survive and reports
// whichever don't — used on refetch to preserve as much of the user's
// existing filter selection as possible instead of wiping it outright.
function keepValidValues(selectedStr, validValues) {
	if (!selectedStr) return { kept: '', removed: [] };
	const validSet = new Set(validValues);
	const selected = selectedStr.split(',').map(v => v.trim()).filter(Boolean);
	return {
		kept:    selected.filter(v => validSet.has(v)).join(','),
		removed: selected.filter(v => !validSet.has(v)),
	};
}

const beacon4FiltersSlice = createSlice({
	name: 'beacon4Filters',
	initialState: {
		dateFrom:    moment().subtract(1, 'months').format('YYYY-MM-DD'),
		dateTo:      moment().format('YYYY-MM-DD'),
		aggregation: 'Daily',

		perfTech:            '2G',
		perfMainKpi:         '',
		perfTableKpi:        '',   // KPI shown in the aggregation table; defaults to Main KPI on Apply, then independent
		perfAggregationMode: 'Site',
		adminDialogOpen:     false,
		// Legend panel visibility. Defaults to visible.
		legendVisible:       true,
		// Activities panel visibility. Defaults to hidden — opt-in overlay
		// that reuses Activity Tracker data (see loadBeacon4PerformanceNetworkOverviewActivities above).
		activitiesVisible:   false,

		// Side/Polygon/Vendor/JV ID selections — scoped to Tech+KPI+date range.
		perfSide:    '',
		perfPolygon: '',
		perfVendor:  '',
		perfJvid:    '',
		// Which dimensions the CHART's own series should be broken out by —
		// the new "Data Aggregation" FilterBar group, comma-joined subset of
		// "Side","Polygon","Vendor","Kpi" (same convention as every other
		// multiselect here). "Kpi" is deliberately NOT part of the default —
		// it isn't a user-facing aggregation choice the same way Side/Polygon/
		// Vendor are; it's forced on server-side whenever a panel ends up with
		// more than one KPI (Main + Breakdown/Breakdown2 — see
		// Beacon4PerformanceNetworkOverviewKpiDataService.BuildSeriesAsync),
		// and stays off otherwise. Default is just Side+Vendor, matching the
		// previously-hardcoded behaviour. Separate from perfAggregationMode
		// above, which only drives the breakdown TABLE at the bottom of the
		// screen, not the chart.
		perfDataAggregation: 'Side,Vendor',
		perfFilterPairs:    [],   // raw pairs for the current Tech+KPI+date scope
		perfFiltersViewName: '',  // last-seen source view (informational only now)
		perfSides:    [],
		perfPolygons: [],
		perfVendors:  [],
		perfJvids:    [],
		perfFiltersLoading: false,
		perfFiltersError:   null,
		// Values dropped on the last refetch because they no longer exist in
		// the new source — e.g. [{ field: 'Polygon', values: ['POLY_12'] }].
		// Drives a dismissible notice in the FilterBar so a selection change
		// mid-analysis doesn't look like a silent bug.
		perfFiltersRemoved: [],

		// Activities overlay — Team > Type > Activity cascade (mirrors Side >
		// Polygon > Vendor > JV ID below), scoped to the Tech chosen in KPI
		// Selection plus the list of ActivityMarkerDto fetched for the current
		// dateFrom/dateTo window (see loadBeacon4PerformanceNetworkOverviewActivities).
		// All comma-joined, same convention as perfSide/perfPolygon/etc.
		perfActivityTeam:      '',
		perfActivityType:      '',
		perfActivities:        '',
		perfActivitiesList:    [],
		perfActivitiesLoading: false,
		perfActivitiesError:   null,
		// "Click a label to filter" isolate state for the Legend panel's
		// Activities section — same shape/convention as visibleSeriesLeft/Right
		// below (null = show all currently selected activities; an array =
		// show only the activity ids in it). Kept separate from perfActivities
		// (the dropdown selection) since isolating one in the legend is a
		// temporary view-only action, not a change to what's actually selected.
		visibleActivities: null,

		visibleSeries: null,
		visibleSeriesLeft:  null,
		visibleSeriesRight: null,
		loading:       false,
		error:         null,
	},
	reducers: {
		// `action.payload` is `{ tech, availableKeys }`, where availableKeys is
		// every KPI key (Main + Breakdown) that exists for the tech being
		// switched to — computed by the caller from perfKpisByTech, since this
		// slice doesn't otherwise know about the KPI catalogue. Kept as an
		// object (rather than plain `tech` string) specifically so both
		// call sites can supply that list and get the same preserve-if-valid
		// treatment, instead of one of them always hard-resetting.
		setBeacon4PerfTech(state, action) {
			const { tech, availableKeys = [] } = action.payload;
			state.perfTech = tech;

			// Activities' Team/Type/Activity cascade is scoped to Tech too
			// (every Activity Tracker entry carries its own Technology) —
			// recompute valid options for the NEW tech and drop whatever no
			// longer qualifies, walking top-down same as the
			// Side/Polygon/Vendor/JV ID cascade below.
			const teamCascade = computeActivityCascade(state.perfActivitiesList, { tech, team: '', type: '' });
			const activityTeamResult = keepValidValues(state.perfActivityTeam, teamCascade.teams);
			state.perfActivityTeam = activityTeamResult.kept;

			const typeCascade = computeActivityCascade(state.perfActivitiesList, { tech, team: state.perfActivityTeam, type: '' });
			const activityTypeResult = keepValidValues(state.perfActivityType, typeCascade.types);
			state.perfActivityType = activityTypeResult.kept;

			const validActivityIds = new Set(
				computeActivityCascade(state.perfActivitiesList, { tech, team: state.perfActivityTeam, type: state.perfActivityType })
					.activities.map(a => String(a.id))
			);
			state.perfActivities = state.perfActivities.split(',').filter(Boolean).filter(id => validActivityIds.has(id)).join(',');

			// Many KPIs (especially Main KPIs like AVAIL) exist across every
			// Tech, so switching Tech doesn't always mean the current KPI
			// stops making sense — only reset it (and, as a consequence, the
			// whole filter cascade below it) when it genuinely isn't offered
			// for the new Tech. This mirrors the same principle as the
			// Side/Polygon/Vendor/JV ID preservation on view change: don't
			// throw away a selection just because something upstream of it
			// changed, if it's still valid.
			const keepMainKpi  = !!state.perfMainKpi  && availableKeys.includes(state.perfMainKpi);
			const keepTableKpi = !!state.perfTableKpi && availableKeys.includes(state.perfTableKpi);

			if (!keepMainKpi) {
				state.perfMainKpi = '';
				// No Main KPI ⇒ no source view to scope the filter cascade to,
				// so it has to be cleared regardless of whether individual
				// Side/Polygon/Vendor/JV ID values would otherwise still be
				// valid — there's nothing left to validate them against.
				state.perfFiltersViewName = '';
				state.perfFilterPairs = [];
				state.perfSide = ''; state.perfPolygon = ''; state.perfVendor = ''; state.perfJvid = '';
				state.perfSides = []; state.perfPolygons = []; state.perfVendors = []; state.perfJvids = [];
			}
			// else: Main KPI still valid for the new Tech — leave it and the
			// current filter selections alone. The FilterBar effect refetches
			// Beacon4PerformanceNetworkOverview_filters_data for (tech, mainKpi),
			// and the existing keep-valid cascade in
			// loadBeacon4PerformanceNetworkOverviewFilters.fulfilled decides
			// what actually survives against that source.

			if (!keepTableKpi) state.perfTableKpi = '';
		},
		setBeacon4PerfMainKpi(state, action)        { state.perfMainKpi         = action.payload; },
		setBeacon4PerfTableKpi(state, action)       { state.perfTableKpi        = action.payload; },
		setBeacon4PerfAggregationMode(state, action){ state.perfAggregationMode = action.payload; },
		setBeacon4AdminDialog(state, action) { state.adminDialogOpen = action.payload; },
		toggleBeacon4Legend(state) { state.legendVisible = !state.legendVisible; },
		// Turning Activities OFF resets the legend's isolate state too — an
		// isolate scoped to activities that are about to stop being shown
		// anywhere (chart markers AND the Legend panel's Activities half both
		// disappear, see selectBeacon4EffectiveActivities/the view) shouldn't
		// silently persist and surprise the user when they turn it back on.
		toggleBeacon4Activities(state) {
			state.activitiesVisible = !state.activitiesVisible;
			state.visibleActivities = null;
		},
		// Team > Type > Activity — changing Team invalidates whatever was
		// picked in Type and Activity below it, same cascade-reset convention
		// as Side resetting Polygon/Vendor/JV ID below. Drops any legend
		// isolate too — it's scoped to the OLD set of activities and wouldn't
		// map meaningfully onto whatever the new selection resolves to.
		setBeacon4PerfActivityTeam(state, action) {
			state.perfActivityTeam = action.payload;
			state.perfActivityType = '';
			state.perfActivities = '';
			state.visibleActivities = null;
		},
		// Type > Activity — changing Type invalidates whatever was picked in
		// Activity below it, same cascade-reset convention as Side resetting
		// Polygon/Vendor/JV ID below. Both also drop any legend isolate — it's
		// scoped to the OLD set of activities and wouldn't map meaningfully
		// onto whatever the new Type/Activity selection resolves to.
		setBeacon4PerfActivityType(state, action) {
			state.perfActivityType = action.payload;
			state.perfActivities = '';
			state.visibleActivities = null;
		},
		setBeacon4PerfActivities(state, action) {
			state.perfActivities = action.payload;
			state.visibleActivities = null;
		},
		// "Click a label to filter" for the Legend panel's Activities section —
		// same additive isolate-toggle as toggleBeacon4PanelSeries above:
		// clicking one activity isolates it (implicitly hides the rest);
		// clicking further activities adds them to the visible set; clicking
		// an already-visible one removes it, reverting to "show all" once the
		// set empties out.
		toggleBeacon4ActivityVisibility(state, action) {
			const key = String(action.payload);
			const current = state.visibleActivities;
			if (current === null) {
				state.visibleActivities = [key];
			} else if (current.includes(key)) {
				const next = current.filter(k => k !== key);
				state.visibleActivities = next.length > 0 ? next : null;
			} else {
				state.visibleActivities = [...current, key];
			}
		},
		resetBeacon4ActivityVisibility(state) { state.visibleActivities = null; },

		setBeacon4Aggregation(state, action) { state.aggregation = action.payload; },
		setBeacon4DateFrom(state, action)    { state.dateFrom    = action.payload; },
		setBeacon4DateTo(state, action)      { state.dateTo      = action.payload; },

		// ── Side/Polygon/Vendor/JV ID (scoped to perfFilterPairs) ────────────
		setBeacon4PerfSide(state, action) {
			state.perfSide    = action.payload;
			state.perfPolygon = '';
			state.perfVendor  = '';
			state.perfJvid    = '';
			const c = computePerfCascade(state.perfFilterPairs, { side: action.payload, polygon: '', vendor: '' });
			state.perfSides = c.sides; state.perfPolygons = c.polygons;
			state.perfVendors = c.vendors; state.perfJvids = c.jvids;
		},
		setBeacon4PerfPolygon(state, action) {
			state.perfPolygon = action.payload;
			state.perfVendor  = '';
			state.perfJvid    = '';
			const c = computePerfCascade(state.perfFilterPairs, { side: state.perfSide, polygon: action.payload, vendor: '' });
			state.perfSides = c.sides; state.perfPolygons = c.polygons;
			state.perfVendors = c.vendors; state.perfJvids = c.jvids;
		},
		setBeacon4PerfVendor(state, action) {
			state.perfVendor = action.payload;
			state.perfJvid   = '';
			const c = computePerfCascade(state.perfFilterPairs, { side: state.perfSide, polygon: state.perfPolygon, vendor: action.payload });
			state.perfSides = c.sides; state.perfPolygons = c.polygons;
			state.perfVendors = c.vendors; state.perfJvids = c.jvids;
		},
		// JV ID sits at the bottom of the cascade — nothing depends on it, so
		// picking one doesn't need to recompute anything else.
		setBeacon4PerfJvid(state, action) { state.perfJvid = action.payload; },

		// "Data Aggregation" — no cascade, just a flat multiselect of which
		// dimensions the chart groups by. "Kpi" is force-included server-side
		// whenever a panel has more than one KPI (Main + Breakdown/Breakdown2)
		// regardless of what's stored here — see
		// Beacon4PerformanceNetworkOverviewKpiDataService.BuildSeriesAsync.
		setBeacon4PerfDataAggregation(state, action) { state.perfDataAggregation = action.payload; },

		// Dismiss the "some filters were removed" notice once the user has
		// seen it. Doesn't touch the filters themselves, just the notice.
		clearBeacon4PerfFiltersRemoved(state) { state.perfFiltersRemoved = []; },

		toggleBeacon4Series(state, action) {
			const key = action.payload;
			if (state.visibleSeries === null) {
				state.visibleSeries = [key];
			} else if (state.visibleSeries.includes(key)) {
				const next = state.visibleSeries.filter(k => k !== key);
				state.visibleSeries = next.length > 0 ? next : null;
			} else {
				state.visibleSeries = [...state.visibleSeries, key];
			}
		},
		// Panel-scoped toggle ('left' = KPI Performance, 'right' = KPI Performance (Aux))
		toggleBeacon4PanelSeries(state, action) {
			const { panel, key } = action.payload;
			const field = panel === 'right' ? 'visibleSeriesRight' : 'visibleSeriesLeft';
			const current = state[field];
			if (current === null) {
				state[field] = [key];
			} else if (current.includes(key)) {
				const next = current.filter(k => k !== key);
				state[field] = next.length > 0 ? next : null;
			} else {
				state[field] = [...current, key];
			}
		},
		resetBeacon4HiddenSeries(state) {
			state.visibleSeries      = null;
			state.visibleSeriesLeft  = null;
			state.visibleSeriesRight = null;
		},
	},
	extraReducers: (b) => {
		b.addCase(loadBeacon4PerformanceNetworkOverviewFilters.pending, (s) => { s.perfFiltersLoading = true; s.perfFiltersError = null; })
		 .addCase(loadBeacon4PerformanceNetworkOverviewFilters.fulfilled, (s, a) => {
				s.perfFiltersLoading = false;
				const { viewName, pairs } = a.payload ?? {};
				s.perfFiltersViewName = viewName ?? '';
				s.perfFilterPairs = pairs ?? [];

				// Rather than wiping the whole Side/Polygon/Vendor/JV ID
				// selection whenever the source view changes, validate each
				// level of the cascade against the NEW pairs and keep
				// whatever still exists. Losing filters mid-analysis is
				// disruptive, and different KPIs often still share most of
				// the same Side/Polygon universe even when their source view
				// differs. A value that no longer exists invalidates
				// whatever was chosen below it in the cascade — same as if
				// the user had picked a new value there by hand — so this
				// walks top-down, re-deriving the valid-options list at each
				// step from whatever survived the level above.
				let c = computePerfCascade(s.perfFilterPairs, { side: '', polygon: '', vendor: '' });
				const sideResult = keepValidValues(s.perfSide, c.sides);
				s.perfSide = sideResult.kept;

				c = computePerfCascade(s.perfFilterPairs, { side: s.perfSide, polygon: '', vendor: '' });
				const polygonResult = keepValidValues(s.perfPolygon, c.polygons);
				s.perfPolygon = polygonResult.kept;

				c = computePerfCascade(s.perfFilterPairs, { side: s.perfSide, polygon: s.perfPolygon, vendor: '' });
				const vendorResult = keepValidValues(s.perfVendor, c.vendors);
				s.perfVendor = vendorResult.kept;

				c = computePerfCascade(s.perfFilterPairs, { side: s.perfSide, polygon: s.perfPolygon, vendor: s.perfVendor });
				const jvidResult = keepValidValues(s.perfJvid, c.jvids);
				s.perfJvid = jvidResult.kept;

				s.perfSides = c.sides; s.perfPolygons = c.polygons;
				s.perfVendors = c.vendors; s.perfJvids = c.jvids;

				// Surface exactly what got dropped (if anything) — silently
				// changing the user's selection under them would otherwise
				// look like a bug rather than an expected consequence of the
				// KPI/date range they just changed.
				s.perfFiltersRemoved = [
					sideResult.removed.length    > 0 ? { field: 'Side',    values: sideResult.removed }    : null,
					polygonResult.removed.length > 0 ? { field: 'Polygon', values: polygonResult.removed } : null,
					vendorResult.removed.length  > 0 ? { field: 'Vendor',  values: vendorResult.removed }  : null,
					jvidResult.removed.length    > 0 ? { field: 'JV ID',   values: jvidResult.removed }    : null,
				].filter(Boolean);
			})
		 .addCase(loadBeacon4PerformanceNetworkOverviewFilters.rejected, (s, a) => { s.perfFiltersLoading = false; s.perfFiltersError = a.payload; });

		b.addCase(loadBeacon4PerformanceNetworkOverviewActivities.pending, (s) => { s.perfActivitiesLoading = true; s.perfActivitiesError = null; })
		 .addCase(loadBeacon4PerformanceNetworkOverviewActivities.fulfilled, (s, a) => {
				s.perfActivitiesLoading = false;
				s.perfActivitiesList = a.payload ?? [];

				// Same "keep only what's still valid" principle as the
				// Side/Polygon/Vendor/JV ID cascade above — Team/Type/Activity
				// selections that no longer qualify under the new date
				// window (fully contained) or the current Tech silently
				// drop out instead of pointing at nothing.
				const teamCascade = computeActivityCascade(s.perfActivitiesList, { tech: s.perfTech, team: '', type: '' });
				const activityTeamResult = keepValidValues(s.perfActivityTeam, teamCascade.teams);
				s.perfActivityTeam = activityTeamResult.kept;

				const typeCascade = computeActivityCascade(s.perfActivitiesList, { tech: s.perfTech, team: s.perfActivityTeam, type: '' });
				const activityTypeResult = keepValidValues(s.perfActivityType, typeCascade.types);
				s.perfActivityType = activityTypeResult.kept;

				const validIds = new Set(
					computeActivityCascade(s.perfActivitiesList, { tech: s.perfTech, team: s.perfActivityTeam, type: s.perfActivityType })
						.activities.map((m) => String(m.id))
				);
				s.perfActivities = s.perfActivities
					.split(',')
					.filter(Boolean)
					.filter((id) => validIds.has(id))
					.join(',');

				// The list just got refetched (date/side/vendor change) — any
				// legend isolate scoped to the OLD set may no longer map onto
				// anything real, so drop it rather than leave a stale filter
				// silently hiding activities the user never meant to hide.
				s.visibleActivities = null;
			})
		 .addCase(loadBeacon4PerformanceNetworkOverviewActivities.rejected, (s, a) => { s.perfActivitiesLoading = false; s.perfActivitiesError = a.payload; });
	},
});

export const {
	setBeacon4PerfTech, setBeacon4PerfMainKpi, setBeacon4PerfTableKpi, setBeacon4PerfAggregationMode,
	setBeacon4AdminDialog,
	setBeacon4Aggregation,
	setBeacon4DateFrom, setBeacon4DateTo,
	setBeacon4PerfSide, setBeacon4PerfPolygon, setBeacon4PerfVendor, setBeacon4PerfJvid,
	setBeacon4PerfDataAggregation,
	clearBeacon4PerfFiltersRemoved,
	toggleBeacon4Legend,
	toggleBeacon4Activities, setBeacon4PerfActivityTeam, setBeacon4PerfActivityType, setBeacon4PerfActivities,
	toggleBeacon4ActivityVisibility, resetBeacon4ActivityVisibility,
	toggleBeacon4Series, toggleBeacon4PanelSeries, resetBeacon4HiddenSeries,
} = beacon4FiltersSlice.actions;

export default beacon4FiltersSlice.reducer;

export const selectBeacon4Filters             = (state) => state.beacon4Filters;
export const selectBeacon4VisibleSeries       = (state) => state.beacon4Filters.visibleSeries;
export const selectBeacon4PerfTech            = (state) => state.beacon4Filters.perfTech;
export const selectBeacon4PerfMainKpi         = (state) => state.beacon4Filters.perfMainKpi;
export const selectBeacon4PerfTableKpi        = (state) => state.beacon4Filters.perfTableKpi;
export const selectBeacon4PerfAggregationMode = (state) => state.beacon4Filters.perfAggregationMode;
export const selectBeacon4AdminDialogOpen = (state) => state.beacon4Filters.adminDialogOpen;
export const selectBeacon4LegendVisible   = (state) => state.beacon4Filters.legendVisible;
export const selectBeacon4VisibleSeriesLeft  = (state) => state.beacon4Filters.visibleSeriesLeft;
export const selectBeacon4VisibleSeriesRight = (state) => state.beacon4Filters.visibleSeriesRight;
export const selectBeacon4VisibleActivities  = (state) => state.beacon4Filters.visibleActivities;
export const selectBeacon4PerfFiltersRemoved = (state) => state.beacon4Filters.perfFiltersRemoved;
export const selectBeacon4PerfDataAggregation = (state) => state.beacon4Filters.perfDataAggregation;

// ── Activities overlay ──────────────────────────────────────────────────────
export const selectBeacon4ActivitiesVisible    = (state) => state.beacon4Filters.activitiesVisible;
export const selectBeacon4PerfActivityTeam     = (state) => state.beacon4Filters.perfActivityTeam;
export const selectBeacon4PerfActivityType     = (state) => state.beacon4Filters.perfActivityType;
export const selectBeacon4PerfActivities       = (state) => state.beacon4Filters.perfActivities;
export const selectBeacon4PerfActivitiesList   = (state) => state.beacon4Filters.perfActivitiesList;
export const selectBeacon4PerfActivitiesLoading = (state) => state.beacon4Filters.perfActivitiesLoading;

// Team options — scoped to the current Tech only (Team has no selection
// upstream of it besides Tech, same role Side plays for the other cascade —
// it sits ABOVE Type, so unlike Type's own options it isn't narrowed by Type).
export const selectBeacon4ActivityTeamOptions = (state) => {
	const { perfActivitiesList, perfTech } = state.beacon4Filters;
	return computeActivityCascade(perfActivitiesList, { tech: perfTech, team: '', type: '' }).teams;
};

// Type options — scoped to Tech AND the selected Team(s).
export const selectBeacon4ActivityTypeOptions = (state) => {
	const { perfActivitiesList, perfTech, perfActivityTeam } = state.beacon4Filters;
	return computeActivityCascade(perfActivitiesList, { tech: perfTech, team: perfActivityTeam, type: '' }).types;
};

// Activity options — the raw ActivityMarkerDto objects scoped to Tech AND
// the selected Team(s)/Type(s). The FilterBar formats these into dropdown labels.
export const selectBeacon4ActivityOptions = (state) => {
	const { perfActivitiesList, perfTech, perfActivityTeam, perfActivityType } = state.beacon4Filters;
	return computeActivityCascade(perfActivitiesList, { tech: perfTech, team: perfActivityTeam, type: perfActivityType }).activities;
};

// Resolved activity objects for whatever is currently selected in the
// multiselect — this is what gets handed to the chart to draw markers (and,
// via assignActivityColors, to the Legend panel's Activities section too).
// perfActivities === '' means the Activity dropdown is at its default
// "(All)" state (nothing explicitly picked) — same convention as every
// other multiselect in this module (Side/Polygon/Vendor/JV ID, Team, Type) —
// so it's treated as "no filter", i.e. every activity currently offered by
// the Team/Type/Tech cascade (selectBeacon4ActivityOptions) is shown, not zero.
export const selectBeacon4SelectedActivities = (state) => {
	const { perfActivitiesList, perfActivities, perfTech, perfActivityTeam, perfActivityType } = state.beacon4Filters;
	const cascopedActivities = computeActivityCascade(perfActivitiesList, { tech: perfTech, team: perfActivityTeam, type: perfActivityType }).activities;
	if (!perfActivities) return cascopedActivities;
	const selectedSet = new Set(perfActivities.split(',').map(v => v.trim()).filter(Boolean));
	return cascopedActivities.filter((a) => selectedSet.has(String(a.id)));
};
